export interface ArchitectureSource {
  architecture?: string | null;
  supported_architectures?: string[] | null;
  supportedArchitectures?: string[] | null;
}

interface EnvironmentArchitectureSource {
  architecture?: string | null;
  labels?: Record<string, string>;
  instance?: {
    labels?: Record<string, string>;
    instance_type_info?: ArchitectureSource;
  };
  instance_type?: ArchitectureSource;
}

const architectureAliases: Record<string, string> = {
  amd64: "x86_64",
  x64: "x86_64",
  "x86-64": "x86_64",
  x86_64: "x86_64",
  aarch64: "ARM64",
  arm64: "ARM64",
  arm: "ARM",
};

export const formatArchitecture = (architecture: string): string => {
  const trimmedArchitecture = architecture.trim();
  if (!trimmedArchitecture) return "";

  return (
    architectureAliases[trimmedArchitecture.toLowerCase()] ||
    trimmedArchitecture
  );
};

export const normalizeArchitectures = (
  architectures: Array<string | null | undefined>,
): string[] =>
  Array.from(
    new Set(
      architectures
        .map((architecture) =>
          architecture ? formatArchitecture(architecture) : "",
        )
        .filter(Boolean),
    ),
  );

export const isArmArchitecture = (architecture?: string | null): boolean =>
  architecture?.toLowerCase().includes("arm") ?? false;

export const getInstanceTypeArchitectures = (
  instanceType?: ArchitectureSource | null,
): string[] => {
  if (!instanceType) return [];

  return normalizeArchitectures([
    instanceType.architecture,
    ...(instanceType.supported_architectures || []),
    ...(instanceType.supportedArchitectures || []),
  ]);
};

export const getArchitectureMismatchWarning = (
  selectedInstanceType?: ArchitectureSource | null,
  defaultInstanceType?: ArchitectureSource | null,
): string | null => {
  const selectedArchitectures =
    getInstanceTypeArchitectures(selectedInstanceType);
  const defaultArchitectures =
    getInstanceTypeArchitectures(defaultInstanceType);

  if (
    selectedArchitectures.length === 0 ||
    defaultArchitectures.length === 0 ||
    selectedArchitectures.some((architecture) =>
      defaultArchitectures.includes(architecture),
    )
  ) {
    return null;
  }

  return `Performance may be degraded because the selected instance type uses ${selectedArchitectures.join(" / ")} architecture, while the launchable's default instance type uses ${defaultArchitectures.join(" / ")}.`;
};

export const getEnvironmentArchitectures = (
  environment?: EnvironmentArchitectureSource | null,
): string[] => {
  if (!environment) return [];

  const selectedArchitectures = normalizeArchitectures([
    environment.architecture,
    environment.instance?.labels?.architecture,
    environment.labels?.architecture,
  ]);

  if (selectedArchitectures.length > 0) return selectedArchitectures;

  return normalizeArchitectures([
    ...getInstanceTypeArchitectures(environment.instance?.instance_type_info),
    ...getInstanceTypeArchitectures(environment.instance_type),
  ]);
};
